/**
 * Clasificación de audiencia de los artículos de Insights.
 *
 * El público objetivo indexable es el comercio a pie de calle en España
 * (abrir local, personal, margen, hostelería, errores de capital). Los
 * artículos corporativos (M&A, lujo B2B, due diligence, hubs de innovación,
 * EBITDA de gran cuenta) siguen accesibles pero no compiten en buscadores:
 * se emiten con `noindex, follow` y se quedan fuera del sitemap, y en el
 * listado aparecen detrás de los de comercio de calle.
 */

/** Slugs de categoría íntegramente corporativos. */
const CORPORATE_CATEGORIES = new Set(["m-and-a"]);

/** Señales de contenido corporativo en el título / palabras clave. */
const CORPORATE_PATTERNS = [
  "m&a",
  "due diligence",
  "ebitda",
  "lujo",
  "luxury",
  "b2b",
  "flagship",
  "hub de innovación",
  "hubs de innovación",
  "innovation hub",
  "master franquicia",
  "master franchise",
  "franquicia",
  "franchise",
  "corporativ",
  "corporate",
  "deuda senior",
  "senior debt",
  "clv",
  "geopolític",
  "geopolit",
  "gobernanza",
  "governance",
  "multinacional",
  "multinational",
  "private equity",
  "family office",
  "expansión internacional",
  "international expansion",
  "expansión global",
  "global expansion",
  "mercados extranjeros",
  "foreign markets",
  "capital privado",
  "private capital",
  "farmac",
  "pharmac",
  "centros de distribución",
  "distribution cent",
  "cadena de suministro",
  "supply chain",
  "gourmet",
  "omnicanal",
  "omnichannel",
  "cartera inmobiliaria",
  "carteras inmobiliarias",
  "real estate portfolio",
  "gran escala",
  "at scale",
  "huella de carbono",
  "carbon footprint",
  "startups",
  "ceo",
  "sucesión",
  "succession",
];

export interface ArticleAudienceInput {
  titleEs?: string | null | undefined;
  titleEn?: string | null | undefined;
  keywords?: string | null | undefined;
  categorySlug?: string | null | undefined;
}

/** `true` si el artículo se dirige a gran cuenta / corporativo. */
export function isCorporateArticle(input: ArticleAudienceInput): boolean {
  if (input.categorySlug && CORPORATE_CATEGORIES.has(input.categorySlug)) return true;
  // Sólo el título: las palabras clave de la ficha son demasiado amplias y
  // marcaban como corporativos artículos de comercio de calle.
  const haystack = [input.titleEs, input.titleEn]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (!haystack) return false;
  return CORPORATE_PATTERNS.some((p) => haystack.includes(p));
}

/**
 * Ordena dejando delante los artículos de comercio a pie de calle y
 * conservando el orden original (fecha desc) dentro de cada grupo.
 */
export function streetRetailFirst<T>(
  items: T[],
  toInput: (item: T) => ArticleAudienceInput,
): T[] {
  const street: T[] = [];
  const corporate: T[] = [];
  for (const item of items) {
    (isCorporateArticle(toInput(item)) ? corporate : street).push(item);
  }
  return [...street, ...corporate];
}
